import { collection, addDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage, auth } from "../lib/firebase";
import { Answer, StorageUploadResult, UserProfile } from "../types";
import { sanitizeForFirestore } from "../utils/firestoreUtils";

const BUCKET_NAME = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "mlc-proposal-hq-storage";

export type PastProposalOutcome = "won" | "submitted" | "lost";

export interface DocumentHarvestCandidate {
  id: string;
  title: string;
  body: string;
  sanitizedBody: string;
  serviceTags: string[];
  rationale: string;
  selected?: boolean;
  similarToAnswerId?: string;
  similarToTitle?: string;
  similarityScore?: number;
}

export interface AnalyzePastProposalParams {
  documentPaths: string[];
  proposalTitle: string;
  clientName?: string;
  outcome?: PastProposalOutcome;
}

export interface SaveHarvestedAnswersMetadata {
  proposalTitle: string;
  clientName?: string;
  outcome?: PastProposalOutcome;
  ownerName: string;
  ownerEmail: string;
  cadenceDays?: number;
  documentPath?: string;
}

/**
 * Uploads a past proposal document (.docx, .pdf) to the storage bucket
 */
export async function uploadPastProposalDocument(
  file: File,
  user: UserProfile,
): Promise<StorageUploadResult> {
  const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const uniqueName = `${Date.now()}_${sanitizedFileName}`;
  const storagePath = `rfps/library_imports/${uniqueName}`;
  const storageRef = ref(storage, storagePath);

  const snapshot = await uploadBytes(storageRef, file, {
    contentType: file.type || "application/octet-stream",
    customMetadata: {
      originalName: file.name,
      uploadedBy: user.email,
      uploadedAt: new Date().toISOString(),
      kind: "past_proposal_import",
    },
  });

  const downloadUrl = await getDownloadURL(storageRef);

  return {
    downloadUrl,
    storagePath: snapshot.metadata.fullPath,
    fullGsPath: `gs://${BUCKET_NAME}/${snapshot.metadata.fullPath}`,
    fileName: file.name,
    fileSize: file.size,
    contentType: file.type || "application/octet-stream",
  };
}

/**
 * Invokes phq_harvestDocument to analyze a past proposal and extract modular answers
 */
export async function analyzePastProposalDocument(
  params: AnalyzePastProposalParams,
): Promise<DocumentHarvestCandidate[]> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error("You must be signed in to analyze past proposal documents.");
  }
  const token = await currentUser.getIdToken();

  const baseUrl =
    import.meta.env.VITE_FUNCTIONS_BASE_URL ||
    "https://us-central1-in-house-dev-mlc.cloudfunctions.net";
  const url = `${baseUrl.replace(/\/$/, "")}/phq_harvestDocument`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      documentPaths: params.documentPaths,
      proposalTitle: params.proposalTitle,
      clientName: params.clientName,
      outcome: params.outcome,
    }),
  });

  if (!response.ok) {
    const errorBody = await response.text();
    let parsedMessage = errorBody;
    try {
      const json = JSON.parse(errorBody);
      if (json.error) parsedMessage = json.error;
    } catch {
      // Keep raw message if non-JSON
    }
    throw new Error(`Failed to extract answers (${response.status}): ${parsedMessage}`);
  }

  const data = await response.json();
  const rawCandidates = Array.isArray(data.candidates) ? data.candidates : [];

  return rawCandidates.map(
    (c: {
      id?: string;
      title?: string;
      body?: string;
      sanitizedBody?: string;
      serviceTags?: string[];
      rationale?: string;
    }) => ({
      id: c.id || `cand-${Math.random().toString(36).substring(2, 9)}`,
      title: c.title || "Untitled Answer",
      body: c.body || "",
      sanitizedBody: c.sanitizedBody || c.body || "",
      serviceTags: Array.isArray(c.serviceTags) ? c.serviceTags : ["General"],
      rationale: c.rationale || "Extracted from past proposal submission.",
      selected: true,
    }),
  );
}

/**
 * Persists approved answer candidates into the Firestore answers collection
 */
export async function saveHarvestedDocumentAnswers(
  candidates: DocumentHarvestCandidate[],
  metadata: SaveHarvestedAnswersMetadata,
): Promise<string[]> {
  const selected = candidates.filter((c) => c.selected);
  if (selected.length === 0) return [];

  const now = new Date();
  const nowIso = now.toISOString();
  const todayStr = nowIso.slice(0, 10);

  const cadenceDays = metadata.cadenceDays || 180;
  const reviewDueDate = new Date(now.getTime() + cadenceDays * 86400000).toISOString().slice(0, 10);

  const isWon = metadata.outcome === "won";
  const createdIds: string[] = [];

  for (const c of selected) {
    const cleanBody = (c.sanitizedBody || c.body).trim();
    if (!cleanBody) continue;

    const newAnswer: Omit<Answer, "id"> = {
      title: c.title.trim() || "Approved Narrative",
      body: cleanBody,
      serviceTags: c.serviceTags.length > 0 ? c.serviceTags : ["General"],
      owner: metadata.ownerName || "Unassigned",
      ownerEmail: metadata.ownerEmail || "",
      lastVerifiedDate: todayStr,
      reviewDueDate,
      status: "active",
      sourceProposal: metadata.proposalTitle || undefined,
      sourceOutcome:
        metadata.outcome === "won" ? "won" : metadata.outcome === "lost" ? "lost" : null,
      harvestedFrom: {
        type: "past_proposal",
        opportunityTitle: metadata.proposalTitle,
        clientName: metadata.clientName,
        harvestedAt: nowIso,
        documentPath: metadata.documentPath,
      },
      wonBidCount: isWon ? 1 : 0,
      wonBidIds: isWon && metadata.proposalTitle ? [metadata.proposalTitle] : [],
      createdAt: nowIso,
      updatedAt: nowIso,
    };

    const docRef = await addDoc(collection(db, "answers"), sanitizeForFirestore(newAnswer));
    createdIds.push(docRef.id);
  }

  return createdIds;
}
